'use-strict';

import angular from 'angular';

export class MonitoringAPIService {
  constructor($http, toastService, stringService, usersService) {
    'ngInject';

    this.$http = $http;
    this.toastService = toastService;
    this.stringService = stringService;
    this.usersService = usersService;
  }

  /**
   * Add a new `EventRule` to the API server.
   *
   * @param {EventRule} rule
   */
  apiAddEventRule(rule) {
    return new Promise((resolve, reject) => {
      this.$http.post(`${API_URL}/api/monitoring/event-rules`, rule)
        .then(() => resolve())
        .catch((e) => {
          this.apiError('Das Ereignis konnte nicht angelegt werden.');
          return reject(e);
        });
    });
  }

  /**
   * Add a new `EventRule` to the API server.
   *
   * @param {EventRule} rule
   */
  apiUpdateEventRule(rule) {
    return new Promise((resolve, reject) => {
      this.$http.put(`${API_URL}/api/monitoring/event-rules`, rule)
        .then(() => resolve())
        .catch((e) => {
          this.apiError('Das Ereignis konnte nicht bearbeitet werden.');
          return reject(e);
        });
    });
  }

  /**
   * Delete an existing `EventRule` at the API server.
   *
   * @param {number} ruleId
   */
  apiDeleteEventRule(ruleId) {
    return new Promise((resolve, reject) => {
      this.$http.delete(`${API_URL}/api/monitoring/event-rules/${ruleId}`)
        .then(() => resolve())
        .catch((e) => {
          this.apiError('Die Ereignisregel konnte nicht gelöscht werden.');
          return reject(e);
        });
    });
  }

  /**
   * Ask API server for all `EventRules`.
   */
  apiGetEventRules() {
    return new Promise((resolve, reject) => {
      this.$http.get(`${API_URL}/api/monitoring/event-rules`)
        .then((res) => resolve(res.data))
        .catch((e) => {
          this.apiError('Die Ereignisregeln konnten nicht abgerufen werden.');
          return reject(e);
        });
    });
  }

  /**
   * Ask API server for a single `EventRules`.
   */
  apiGetEventRule(ruleId) {
    return new Promise((resolve, reject) => {
      this.$http.get(`${API_URL}/api/monitoring/event-rules/${ruleId}`)
        .then((res) => resolve(res.data[0]))
        .catch((e) => {
          this.apiError('Die Ereignisregel konnten nicht abgerufen werden.');
          return reject(e);
        });
    });
  }

  /**
   * Ask API server for all `Event`s.
   */
  apiGetEvents() {
    return new Promise((resolve, reject) => {
      this.$http.get(`${API_URL}/api/monitoring/events`)
        .then((res) => resolve(this.prepareEvents(res.data)))
        .catch((e) => {
          this.apiError('Die Ereignisse konnten nicht abgerufen werden.');
          return reject(e);
        });
    });
  }

  apiGetAllEvents() {
    return new Promise((resolve, reject) => {
      this.$http.get(`${API_URL}/api/monitoring/all-events`)
        .then((res) => resolve(this.prepareEvents(res.data)))
        .catch((e) => {
          this.apiError('Die Ereignisse konnten nicht abgerufen werden.');
          return reject(e);
        });
    });
  }

  /**
   * Ask API server for all tasks for current user, that are marked as `open`.
   */
  apiGetOpenTasks() {
    return new Promise((resolve, reject) => {
      this.$http.get(`${API_URL}/api/monitoring/open-tasks`)
        .then((res) => resolve(this.prepareTasks(res.data)))
        .catch((e) => {
          this.apiError('Offene Aufgaben konnten nicht abgerufen werden.');
          return reject(e);
        });
    });
  }

  /**
   * Delete the task with the provided id.
   */
  apiDeleteTask(taskId) {
    return new Promise((resolve, reject) => {
      this.$http.delete(`${API_URL}/api/monitoring/tasks/${taskId}`)
        .then(() => resolve())
        .catch((e) => {
          this.apiError('Die Aufgabe konnte nicht gelöscht werden.');
          return reject(e);
        });
    });
  }

  /**
   * Ask API server for all tasks.
   */
  apiGetAllTasks() {
    return new Promise((resolve, reject) => {
      this.$http.get(`${API_URL}/api/monitoring/tasks`)
        .then((res) => resolve(this.prepareTasks(res.data)))
        .catch((e) => {
          this.apiError('Alle Aufgaben konnten nicht abgerufen werden.');
          return reject(e);
        });
    });
  }

  /**
   * Ask API server for all tasks created by the current user.
   */
  apiGetTasksCreatedByCurrentUser() {
    return new Promise((resolve, reject) => {
      this.$http.get(`${API_URL}/api/monitoring/created-tasks`)
        .then((res) => resolve(this.prepareTasks(res.data)))
        .catch((e) => {
          this.apiError('Erstellte Aufgaben konnten nicht abgerufen werden.');
          return reject(e);
        });
    });
  }

  /**
   * Asks API server for the `Task` with the provided ID.
   *
   * @param {*} taskId
   * @returns {Pomise<Task>}
   */
  apiGetTask(taskId) {
    return new Promise((resolve, reject) => {
      this.$http.get(`${API_URL}/api/monitoring/tasks/${taskId}`)
        .then((res) => {
          if (res.data.length === 0) {
            const e = 'Die Aufgabe konnten nicht abgerufen werden.';
            this.apiError(e);
            return reject(e);
          }
          return resolve(this.prepareTasks([res.data])[0]);
        })
        .catch((e) => {
          this.apiError('Die Aufgabe konnten nicht abgerufen werden.');
          return reject(e);
        });
    });
  }

  /**
   * Update a `Tasks` state.
   *
   * @param {number} taskId
   * @param {string} state
   * @returns {Promise<string>}
   */
  apiUpdateTaskState(taskId, state) {
    return new Promise((resolve, reject) => {
      const postData = {
        taskId,
        state,
      };

      this.$http.put(`${API_URL}/api/monitoring/task-state`, postData)
        .then(() => resolve())
        .catch((e) => {
          this.apiError('Der Status des Auftrags konnte nicht geändert werden.');
          return reject(e);
        });
    });
  }

  /**
   * Add a new `Task` manually to the API server.
   *
   * @param {Task} task
   */
  apiCreateTask(task) {
    return new Promise((resolve, reject) => {
      this.$http.post(`${API_URL}/api/monitoring/tasks`, { task })
        .then((res) => resolve(res.data))
        .catch((e) => {
          this.apiError('Die Aufgabe konnte nicht angelegt werden.');
          return reject(e);
        });
    });
  }

  apiAddTaskComment(taskId, content) {
    return new Promise((resolve, reject) => {
      this.$http.post(`${API_URL}/api/monitoring/task-comment/${taskId}`, { content })
        .then((res) => resolve(res.data))
        .catch((e) => {
          this.apiError('Fehler beim Speichern des Kommentars');
          return reject(e);
        });
    });
  }

  apiRemoveTaskComment(commentId) {
    return new Promise((resolve, reject) => {
      this.$http.delete(`${API_URL}/api/monitoring/task-comment/${commentId}`)
        .then(() => resolve())
        .catch((e) => {
          this.apiError('Fehler beim Löschen des Kommentars');
          return reject(e);
        });
    });
  }

  apiGetCommentsForTask(taskId) {
    return new Promise((resolve, reject) => {
      this.$http.get(`${API_URL}/api/monitoring/task-comment/${taskId}`)
        .then((res) => resolve(res.data))
        .catch((e) => {
          this.apiError('Fehler beim Abrufen der Kommentare');
          return reject(e);
        });
    });
  }

  /**
   * Add additional information required for the display to the supplied events.
   *
   * @param {*} events
   */
  prepareEvents(events) {
    for (const event of events) {
      switch (event.source) {
        case 'cars':
          event.icon = 'fad fa-car';
          event.link = `/vehicles/stock?id=${event.source_id}`;
          event.source_description = 'Fahrzeug';
          break;

        default:
          break;
      }
    }
    return events;
  }

  /**
   * Prepare Array of `Tasks` for display in frontend.
   *
   * @param {Array<Task>} tasks
   * @returns {Array<Task>}
   */
  prepareTasks(tasks) {
    for (const task of tasks) {
      switch (task.state) {
        case 'open':
          task.state_description = 'Offen';
          break;

        case 'closed':
          task.state_description = 'Abgeschlossen';
          break;

        default:
          break;
      }

      switch (task.source) {
        case 'cars':
          task.icon = 'fad fa-car';
          task.link = `/vehicles/stock?id=${task.source_id}`;
          task.source_description = 'Fahrzeug';
          break;

        default:
          break;
      }

      switch (task.type) {
        case 'manual':
          task.card_description = this.stringService.limitString(task.description, 100);
          task.creator_name = this.usersService.getNameOfUser(task.creator);
          task.files = task.files !== undefined && task.files !== null ? task.files.split(',') : null;
          break;

        case 'rule':
          task.title = task.rule_name;
          task.card_description = task.source_name;
          task.description = task.rule_description;
          task.creator_name = 'Automatisch';
          break;

        default:
          break;
      }
    }
    return tasks;
  }

  /**
   * Show an error message and log the error stack to the console
   *
   * @param {string} message
   */
  apiError(message) {
    this.toastService.toast('error', message);
  }
}

export default angular.module('services.monitoringApiService', [])
  .service('monitoringApiService', MonitoringAPIService)
  .name;
